/**
 * EditTripModal Component - Form for editing an existing trip
 *
 * Field order matches Create: Corridor → Partner → Capacity → Vehicle / Driver → rest.
 * Capacity-first: capacityClass preferred for shared loads; vehicle optional unless
 * scheduling with fleet. Both may be set (not XOR).
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Form,
  Select,
  Input,
  Button,
  Space,
  Typography,
  Alert,
  Spin,
  Tag,
  Radio,
} from "antd";
import { EditOutlined, BankOutlined } from "@ant-design/icons";
import { getVehicle, getVehicles } from "../../vehicles/services/vehicleService";
import { getDrivers } from "../../drivers/services/driverService";
import { getOrders } from "../../orders/services/orderService";
import { getActiveRouteCorridors } from "../../routeCorridors/services/routeCorridorService";
import { getPartners } from "../../partners/services/partnerService";
import { getSetting } from "../../systemSettings/services/systemSettingsService.js";
import { updateTrip } from "../services/tripService";
import {
  ACCEPTING_LOAD_TRIP_STATUS,
  PLANNED_TRIP_STATUS,
} from "../constants/tripStatuses.js";
import { logError, logInfo } from "../../../shared/utils/logger.js";
import { filterSelectOption } from "../../../shared/utils/selectFilterOption.js";
import {
  BUBAKII_PROVIDER,
  isBubakiiProvider,
} from "../../drivers/utils/providerDriverAssignment";
import {
  deriveCapacityClassFromVehicle,
  driverFiltersForPartnerSelection,
  FALLBACK_CAPACITY_CLASS_OPTIONS,
  isVehicleRequiredForIntent,
  noFleetScheduleWarning,
  operatorToPartnerSelection,
  partnerHasNoVehicles,
  partnerSelectionToOperator,
  resolveCapacityClassOptions,
  shouldBlockSubmitForMissingFleet,
  shouldShowDriverField,
  shouldShowVehicleField,
  vehicleFiltersForPartnerSelection,
} from "../utils/tripOperatorFields.js";
import DateTimeField from "../../../components/DateTimeField";
import { useDeviceType } from "../../leads/hooks/useDeviceType.js";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

// Helper function to extract province from order location
const getProvince = (location) => {
  // Try addressComponents first (structured data)
  if (location?.addressComponents?.province) {
    return location.addressComponents.province;
  }
  
  // Fallback: try to parse from address string
  if (location?.address) {
    const addressParts = location.address.split(",").map(p => p.trim());
    // Province is usually last or second-to-last part
    const possibleProvince = addressParts[addressParts.length - 1] || 
                           addressParts[addressParts.length - 2];
    return possibleProvince || "N/A";
  }
  
  return "N/A";
};

// Format order display: Order ID - Customer Name - Pickup Province → Delivery Province
const formatOrderDisplay = (order) => {
  const customer = order.customer || "N/A";
  const pickupProvince = getProvince(order.pickup);
  const deliveryProvince = getProvince(order.delivery);
  return `${order.id} - ${customer} - ${pickupProvince} → ${deliveryProvince}`;
};

const EditTripModal = ({ open, trip, onSuccess, onCancel, scheduleMode = false }) => {
  const { isMobile } = useDeviceType();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [orders, setOrders] = useState([]);
  const [routeCorridors, setRouteCorridors] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingRouteCorridors, setLoadingRouteCorridors] = useState(false);
  const [error, setError] = useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState(undefined);
  const [capacityOptions, setCapacityOptions] = useState([
    ...FALLBACK_CAPACITY_CLASS_OPTIONS,
  ]);

  const loadAssetsForPartner = useCallback(
    async (partnerSelectionId, preserveVehicleId = null) => {
      if (!partnerSelectionId) {
        setVehicles([]);
        setDrivers([]);
        return;
      }

      // Edit: include in-use fleet for the partner so the current trip vehicle stays selectable
      const vehicleFilters = vehicleFiltersForPartnerSelection(
        partnerSelectionId,
        {}
      );
      const driverFilters = driverFiltersForPartnerSelection(partnerSelectionId);

      try {
        setLoadingVehicles(true);
        setLoadingDrivers(true);

        const [vehicleResponse, driverResponse] = await Promise.all([
          vehicleFilters
            ? getVehicles(vehicleFilters)
            : Promise.resolve({ success: true, data: [] }),
          driverFilters
            ? getDrivers(driverFilters)
            : Promise.resolve({ success: true, data: [] }),
        ]);

        let nextVehicles =
          vehicleResponse.success && vehicleResponse.data
            ? vehicleResponse.data || []
            : [];

        if (
          preserveVehicleId &&
          !nextVehicles.some((vehicle) => vehicle.id === preserveVehicleId)
        ) {
          try {
            const currentVehicle = await getVehicle(preserveVehicleId);
            if (currentVehicle.success && currentVehicle.data) {
              nextVehicles = [currentVehicle.data, ...nextVehicles];
            }
          } catch (err) {
            logError("Failed to fetch current trip vehicle", {
              error: err.message,
            });
          }
        }

        setVehicles(nextVehicles);
        setDrivers(
          driverResponse.success && driverResponse.data
            ? driverResponse.data || []
            : []
        );
      } catch (err) {
        logError("Failed to fetch partner assets", { error: err.message });
        setVehicles([]);
        setDrivers([]);
      } finally {
        setLoadingVehicles(false);
        setLoadingDrivers(false);
      }
    },
    []
  );

  useEffect(() => {
    if (open && trip) {
      fetchPartners();
      fetchOrders();
      fetchRouteCorridors();
      fetchCapacityOptions();

      // Pre-populate form with trip data
      const orderIds = trip.orders?.map(order =>
        typeof order === 'string' ? order : order?.id
      ).filter(Boolean) || [];

      setSelectedOrderIds(orderIds);
      const partnerSelection = operatorToPartnerSelection(trip);
      setSelectedPartnerId(partnerSelection);
      if (partnerSelection) {
        loadAssetsForPartner(partnerSelection, trip.vehicleId || null);
      } else {
        setVehicles([]);
        setDrivers([]);
      }

      form.setFieldsValue({
        routeCorridor: trip.routeCorridor || "",
        vehicleId: trip.vehicleId || undefined,
        capacityClass: trip.capacityClass || undefined,
        partnerId: partnerSelection,
        driverId: trip.driverId || undefined,
        departureDate: trip.departureDate ? dayjs(trip.departureDate) : null,
        orderIds: orderIds,
        notes: trip.notes || "",
      });

      setError(null);
    }
  }, [open, trip, form, loadAssetsForPartner]);

  const fetchCapacityOptions = async () => {
    try {
      const response = await getSetting("pricing.vehicles.bands");
      const bands = response?.data?.value ?? response?.value ?? response?.data;
      setCapacityOptions(resolveCapacityClassOptions(bands));
    } catch (err) {
      logError("Failed to fetch capacity class options", { error: err.message });
      setCapacityOptions([...FALLBACK_CAPACITY_CLASS_OPTIONS]);
    }
  };

  const fetchPartners = async () => {
    try {
      setLoadingPartners(true);
      const response = await getPartners({ isActive: true });
      if (response.success && response.data) {
        setPartners(response.data || []);
      } else {
        setPartners([]);
      }
    } catch (err) {
      logError("Failed to fetch partners", { error: err.message });
      setPartners([]);
    } finally {
      setLoadingPartners(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoadingOrders(true);
      // Fetch pending and booked orders, including those already assigned to this trip
      const response = await getOrders({ status: "pending,booked" });
      if (response.success && response.data) {
        // Include orders that are either:
        // 1. Not assigned to any trip, OR
        // 2. Already assigned to this trip
        const availableOrders = (response.data || []).filter(
          (order) => !order.tripId || order.tripId === trip?.id
        );
        setOrders(availableOrders);
      }
    } catch (err) {
      logError("Failed to fetch orders", { error: err.message });
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchRouteCorridors = async () => {
    try {
      setLoadingRouteCorridors(true);
      const routeCorridorsList = await getActiveRouteCorridors();
      setRouteCorridors(routeCorridorsList || []);
    } catch (err) {
      logError("Failed to fetch route corridors", { error: err.message });
      setRouteCorridors([]);
    } finally {
      setLoadingRouteCorridors(false);
    }
  };

  const handlePartnerChange = (partnerSelectionId) => {
    setSelectedPartnerId(partnerSelectionId);
    form.setFieldsValue({ vehicleId: undefined, driverId: undefined });
    if (!partnerSelectionId) {
      setVehicles([]);
      setDrivers([]);
      return;
    }
    loadAssetsForPartner(partnerSelectionId, null);
  };

  const handleVehicleChange = (vehicleId) => {
    if (!vehicleId) return;
    const vehicle = vehicles.find((v) => v.id === vehicleId);
    const derived = deriveCapacityClassFromVehicle(vehicle, capacityOptions);
    if (derived) {
      form.setFieldValue("capacityClass", derived);
    }
  };

  const handleSubmit = async (values) => {
    if (!trip?.id) {
      setError("Trip ID is missing");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get current form values directly
      const formValues = form.getFieldsValue();

      // Use formValues.orderIds as fallback if values.orderIds is empty
      // Also check selectedOrderIds state as final fallback
      const orderIds = values.orderIds?.length > 0 
        ? values.orderIds 
        : (formValues.orderIds?.length > 0 
          ? formValues.orderIds 
          : (selectedOrderIds?.length > 0 ? selectedOrderIds : []));

      const isAcceptingLoad = trip.status === ACCEPTING_LOAD_TRIP_STATUS;
      const updateData = {
        notes: values.notes || "",
        orders: orderIds,
      };

      if (!scheduleMode) {
        updateData.routeCorridor = values.routeCorridor;
        updateData.vehicleId = values.vehicleId || null;
        updateData.capacityClass = values.capacityClass || null;
        const { ownerType, partnerId } = partnerSelectionToOperator(
          values.partnerId
        );
        updateData.ownerType = ownerType;
        updateData.partnerId = partnerId;
        updateData.driverId = values.driverId || null;
      }

      if (values.departureDate) {
        updateData.departureDate = values.departureDate.toISOString();
      } else if (!isAcceptingLoad && !scheduleMode) {
        throw new Error("Departure date is required for scheduled trips");
      }

      if (scheduleMode || (isAcceptingLoad && values.departureDate)) {
        updateData.status = PLANNED_TRIP_STATUS;
      }

      const result = await updateTrip(trip.id, updateData);
      if (result.success) {
        logInfo("Trip updated successfully", { tripId: trip.id });
        if (onSuccess) {
          onSuccess(result.data);
        }
      } else {
        setError(result.error || "Failed to update trip");
      }
    } catch (err) {
      logError("Failed to update trip", { error: err.message });
      setError(err.message || "Failed to update trip");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedOrderIds([]);
    setSelectedPartnerId(undefined);
    setVehicles([]);
    setDrivers([]);
    setError(null);
    if (onCancel) {
      onCancel();
    }
  };

  const isAcceptingLoadTrip = trip?.status === ACCEPTING_LOAD_TRIP_STATUS;
  const editIntent = isAcceptingLoadTrip
    ? ACCEPTING_LOAD_TRIP_STATUS
    : PLANNED_TRIP_STATUS;
  const showVehicle =
    !scheduleMode &&
    shouldShowVehicleField(selectedPartnerId, vehicles, loadingVehicles);
  const showDriver =
    !scheduleMode &&
    shouldShowDriverField(selectedPartnerId, drivers, loadingDrivers);
  const noVehiclesForPartner =
    !scheduleMode &&
    partnerHasNoVehicles(selectedPartnerId, vehicles, loadingVehicles);
  const vehicleRequired =
    !scheduleMode && isVehicleRequiredForIntent(editIntent, showVehicle);
  const fleetWarning =
    !scheduleMode && noFleetScheduleWarning(editIntent, noVehiclesForPartner);
  const assetsLoading =
    !scheduleMode &&
    Boolean(selectedPartnerId) &&
    (loadingVehicles || loadingDrivers);
  const blockForMissingFleet = shouldBlockSubmitForMissingFleet();

  return (
    <Modal
      title={
        <Space>
          <EditOutlined style={{ color: "#1890ff" }} />
          <Text strong>
            {scheduleMode ? "Schedule departure" : "Edit Trip"}
          </Text>
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={isMobile ? "calc(100vw - 24px)" : 700}
      centered
      destroyOnHidden
      styles={
        isMobile
          ? {
              body: {
                maxHeight: "calc(100vh - 120px)",
                overflowY: "auto",
                overflowX: "hidden",
                WebkitOverflowScrolling: "touch",
                // Extra space so native date/time pickers near the bottom
                // are less likely to clip against the modal edge.
                paddingBottom: 48,
              },
            }
          : undefined
      }
    >
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
          closable
          onClose={() => setError(null)}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          name="routeCorridor"
          label="Route Corridor"
          hidden={scheduleMode}
          rules={[
            {
              required: !scheduleMode,
              message: "Please select a route corridor",
            },
          ]}
        >
          <Select
            placeholder="Select route corridor"
            showSearch
            loading={loadingRouteCorridors}
            optionFilterProp="label"
            filterOption={filterSelectOption}
            notFoundContent={
              loadingRouteCorridors ? (
                <Spin size="small" />
              ) : routeCorridors.length === 0 ? (
                "No route corridors available"
              ) : null
            }
          >
            {routeCorridors.map((route) => (
              <Option
                key={route.id}
                value={route.name}
                label={
                  route.description
                    ? `${route.name} - ${route.description}`
                    : route.name
                }
              >
                {route.name}
                {route.description && ` - ${route.description}`}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="partnerId"
          label="Partner"
          hidden={scheduleMode}
          rules={[
            {
              required: !scheduleMode,
              message: "Please select a partner",
            },
          ]}
          help={
            scheduleMode
              ? undefined
              : "Who operates this trip — Bubakii fleet or a third-party partner"
          }
        >
          <Select
            placeholder="Select partner"
            showSearch
            loading={loadingPartners}
            optionFilterProp="label"
            filterOption={filterSelectOption}
            optionLabelProp="label"
            onChange={handlePartnerChange}
            data-testid="edit-trip-partner-select"
          >
            {[BUBAKII_PROVIDER, ...partners].map((partner) => (
              <Option
                key={partner.id}
                value={partner.id}
                label={partner.name}
              >
                <Space>
                  <BankOutlined />
                  <Text strong>{partner.name}</Text>
                  <Tag color={isBubakiiProvider(partner) ? "blue" : "orange"}>
                    {isBubakiiProvider(partner) ? "Bubakii" : "3rd Party"}
                  </Tag>
                </Space>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {assetsLoading && (
          <Form.Item>
            <Space>
              <Spin size="small" />
              <Text type="secondary">Loading vehicles and drivers…</Text>
            </Space>
          </Form.Item>
        )}

        {fleetWarning && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              isAcceptingLoadTrip
                ? "No vehicles for this partner"
                : "No truck assigned"
            }
            description={fleetWarning}
            data-testid="edit-trip-no-vehicles"
          />
        )}

        {!scheduleMode && (
          <Form.Item
            name="capacityClass"
            label="Capacity"
            rules={[
              {
                required: true,
                message: "Please select a capacity class",
              },
            ]}
            help={
              isAcceptingLoadTrip
                ? "Required for shared-load matching (marketplace / PWA)"
                : "Auto-fills from vehicle when selected; you can still change it"
            }
          >
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              data-testid="edit-trip-capacity-class"
            >
              {capacityOptions.map((option) => (
                <Radio.Button key={option.value} value={option.value}>
                  {option.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </Form.Item>
        )}

        {showVehicle && (
          <Form.Item
            name="vehicleId"
            label={vehicleRequired ? "Vehicle" : "Vehicle (Optional)"}
            rules={[
              {
                required: vehicleRequired,
                message: "Please select a vehicle",
              },
            ]}
            help={
              vehicleRequired
                ? "Vehicles belonging to the selected partner"
                : "Optional for open shared load — assign a plate when known"
            }
          >
            <Select
              placeholder={
                vehicleRequired
                  ? "Select vehicle"
                  : "Select vehicle (optional)"
              }
              showSearch
              allowClear={!vehicleRequired}
              loading={loadingVehicles}
              optionFilterProp="label"
              filterOption={filterSelectOption}
              onChange={handleVehicleChange}
              data-testid="edit-trip-vehicle-select"
            >
              {vehicles.map((vehicle) => (
                <Option
                  key={vehicle.id}
                  value={vehicle.id}
                  label={`${vehicle.licensePlate} - ${vehicle.make} ${vehicle.model} (${vehicle.vehicleType})`}
                >
                  {vehicle.licensePlate} - {vehicle.make} {vehicle.model} (
                  {vehicle.vehicleType})
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {showDriver && (
          <Form.Item
            name="driverId"
            label="Driver (Optional)"
            help="Optional — assign a driver now or later"
          >
            <Select
              placeholder="Select driver (optional)"
              showSearch
              allowClear
              loading={loadingDrivers}
              optionFilterProp="label"
              filterOption={filterSelectOption}
              data-testid="edit-trip-driver-select"
            >
              {drivers.map((driver) => (
                <Option
                  key={driver.id}
                  value={driver.id}
                  label={`${driver.name} (${driver.phone})`}
                >
                  {driver.name} ({driver.phone})
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="departureDate"
          label="Departure Date & Time"
          rules={[
            {
              required: scheduleMode || !isAcceptingLoadTrip,
              message: "Please select departure date and time",
            },
          ]}
          help={
            isAcceptingLoadTrip && !scheduleMode
              ? "Optional while accepting load — use Schedule departure to lock a date"
              : scheduleMode
                ? "Sets the committed departure and moves the trip to Planned"
                : undefined
          }
        >
          <DateTimeField disablePast aria-label="Departure date and time" />
        </Form.Item>

        <Form.Item 
          name="orderIds" 
          label="Orders (Optional)"
          hidden={scheduleMode}
          getValueFromEvent={(value) => {
            // Ensure we always return an array
            if (!value) return [];
            const result = Array.isArray(value) ? value : [value];
            setSelectedOrderIds(result); // Track for helper text
            return result;
          }}
          help={
            selectedOrderIds.length > 0
              ? `${selectedOrderIds.length} order${selectedOrderIds.length > 1 ? 's' : ''} selected`
              : "Select orders to assign to this trip"
          }
        >
          <Select
            mode="multiple"
            placeholder="Select orders to assign to this trip"
            loading={loadingOrders}
            optionFilterProp="label"
            allowClear
            showSearch
            filterOption={filterSelectOption}
          >
            {orders.map((order) => (
              <Option
                key={order.id}
                value={order.id}
                label={formatOrderDisplay(order)}
              >
                {formatOrderDisplay(order)}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="notes" label="Notes (Optional)" hidden={scheduleMode}>
          <TextArea rows={3} placeholder="Add any notes about this trip..." />
        </Form.Item>

        <Form.Item>
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={blockForMissingFleet || assetsLoading}
              data-testid="edit-trip-submit"
            >
              {scheduleMode ? "Schedule departure" : "Update Trip"}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditTripModal;
